import React, { createContext, useContext, useEffect, useState } from "react";
import { useProfile } from "../../auth/hooks/useProfile";
import { getOrganization } from "../../organization/api/organization.api";

type CoachContextType = {
  organizationId: number | null;
  setOrganizationId: (id: string) => void;
  defaultSurveyId: number | null;
  organization: any | null;
};

const CoachContext = createContext<CoachContextType | null>(null);

const COACH_ORGANIZATION_ID_KEY = "COACH_ORGANIZATION_ID";

export const CoachProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { organizations, isCoach } = useProfile();
  const [organizationId, setOrganizationIdState] = useState<string | null>(
    null,
  );

  const [organization, setOrganization] = useState<any | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(COACH_ORGANIZATION_ID_KEY);

    if (stored) {
      setOrganizationIdState(stored);
    } else if (organizations?.length > 0) {
      const defaultId = String(organizations[0].id);
      setOrganizationIdState(defaultId);
      localStorage.setItem(COACH_ORGANIZATION_ID_KEY, defaultId);
    }
  }, [organizations]);

  useEffect(() => {
    if (!organizationId) return;
    if (!isCoach) return;

    let active = true;

    const fetchOrganization = async () => {
      try {
        const res = await getOrganization(Number(organizationId));
        if (active && res?.success) {
          setOrganization(res.data);
        }
      } catch {
        if (active) setOrganization(null);
      }
    };

    fetchOrganization();

    return () => {
      active = false;
    };
  }, [organizationId, isCoach]);

  const setOrganizationId = (id: string) => {
    setOrganizationIdState(id);
    localStorage.setItem(COACH_ORGANIZATION_ID_KEY, id);
  };

  return (
    <CoachContext.Provider
      value={{
        organizationId: organizationId ? Number(organizationId) : null,
        setOrganizationId,
        defaultSurveyId: organization?.default_survey
          ? Number(organization.default_survey)
          : null,
        organization,
      }}
    >
      {children}
    </CoachContext.Provider>
  );
};

export const useCoach = () => {
  const ctx = useContext(CoachContext);
  if (!ctx) throw new Error("useCoach must be used within CoachProvider");
  return ctx;
};
